import { IKamFlag } from '@/store/kameleoonFlags/kameleoonFlags.interface';
import { getKameleoonClient, getKameleoonFlags } from 'Utils/kameleoon/kameloon-sdk';
import { defaultTracesSampleRatio, loggingUrl, metricsUrl, OtelRemoteConfigs, tracesUrl } from '@/telemetry-sdk';

const fallbackConfigurations = {
  traceSamplingRatio: 0,
  metricsEnabled: false,
  tracingEnabled: false,
  loggingEnabled: false,
  homepageSLO: 0,
  metricsCollectorURL: '',
  tracesCollectorURL: '',
  loggingCollectorURL: '',
};
export const getOtelConfigFromNextData = (): OtelRemoteConfigs => {
  const nextData = window.__NEXT_DATA__;
  if (!nextData || !nextData.props || !nextData.props.pageProps || !nextData.props.pageProps.kamFlags) {
    return fallbackConfigurations;
  }
  const kamFlags = nextData.props.pageProps.kamFlags;
  return convertKamFlagsToOtelRemoteConfigs(kamFlags);
};

export const convertKamFlagsToOtelRemoteConfigs = (
  activeKamFeatureFlags: Record<string, IKamFlag> | null
): OtelRemoteConfigs => {
  if (!activeKamFeatureFlags) {
    return fallbackConfigurations;
  }

  let featureFlagName = 'sdm-web-otel';
  let variableKey = 'sdm_otel';

  const result = Object.entries(activeKamFeatureFlags)
    .filter(([key, exp]: [string, any]) => key === featureFlagName)
    .map(([key, exp]: [string, any]) => {
      return exp.variables;
    })
    .flat()
    .map((variable: any) => variable.value)
    .flat()
    .filter((val) => val && val.featureFlag && val.featureFlag === variableKey)
    .map(
      ({
        featureFlag,
        metricsEnabled,
        tracingEnabled,
        loggingEnabled,
        homepageSlo,
        metricsCollectorURL,
        tracesCollectorURL,
        loggingCollectorURL,
        traceSamplingRatio,
      }) => {
        const boolMetricsEnabled = toBoolean(metricsEnabled);
        const boolTracingEnabled = toBoolean(tracingEnabled);
        const boolLoggingEnabled = toBoolean(loggingEnabled);
        const parsedHomepageSlo = typeof homepageSlo === 'number' ? homepageSlo : 0;
        const parsedSamplingRatio = typeof traceSamplingRatio === 'number' ? traceSamplingRatio : 0;
        const validatedMetricsCollectorURL = validateUrlWithFallback(metricsCollectorURL, metricsUrl);
        const validatedTracesCollectorURL = validateUrlWithFallback(tracesCollectorURL, tracesUrl);
        const validatedLoggingCollectorURL = validateUrlWithFallback(loggingCollectorURL, loggingUrl);
        return {
          name: `bff.exp.${featureFlag}`,
          metricsEnabled: boolMetricsEnabled,
          tracingEnabled: boolTracingEnabled,
          loggingEnabled: boolLoggingEnabled,
          homepageSLO: parsedHomepageSlo,
          traceSamplingRatio: parsedSamplingRatio,
          metricsCollectorURL: validatedMetricsCollectorURL,
          tracesCollectorURL: validatedTracesCollectorURL,
          loggingCollectorURL: validatedLoggingCollectorURL,
        };
      }
    )
    .find((flag) => {
      return flag && flag.name === 'bff.exp.sdm_otel';
    });

  if (!result) {
    return fallbackConfigurations;
  }

  return result;
};

export const getRemoteConfigs = async (userId: string): Promise<OtelRemoteConfigs> => {
  try {
    const kamClient = await getKameleoonClient();
    let activeKamFeatureFlags: Record<string, IKamFlag> | null = null;
    if (kamClient) {
      try {
        activeKamFeatureFlags = await getKameleoonFlags(userId, userId);
      } catch (kamError) {
        console.error('Error fetching Kameleoon flags:', kamError);
        return fallbackConfigurations;
      }
    }

    if (activeKamFeatureFlags == null) {
      return fallbackConfigurations;
    }

    const result = convertKamFlagsToOtelRemoteConfigs(activeKamFeatureFlags);
    return result || { status: false, alwaysOnTracingUserIds: [] };
  } catch (error) {
    console.error('Error fetching remote configs:', error);
    return fallbackConfigurations;
  }
};

const GENERIC_FALLBACK_URL = 'https://localhost';

export function calculateTraceSamplingRatio(remoteConfigs: OtelRemoteConfigs): number {
  if (!remoteConfigs.traceSamplingRatio) {
    return 0;
  }
  const remoteTracingRatio = remoteConfigs?.traceSamplingRatio;
  return remoteTracingRatio != undefined ? remoteTracingRatio : defaultTracesSampleRatio;
}

// This avoids the website crashing due to an invalid URL passed in from remote configurations.
function validateUrlWithFallback(remoteUrl: string, fallbackUrl: string): string {
  if (isValidUrl(remoteUrl)) {
    return remoteUrl;
  } else if (isValidUrl(fallbackUrl)) {
    return fallbackUrl;
  }
  // If remote config local config value for url are invalid,
  // return a generic fallback URL so that the app doesn't crash.
  return GENERIC_FALLBACK_URL;
}

function isValidUrl(url: string) {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

function toBoolean(value: any) {
  return value === true || value === 'true';
}
